import { Component, h, type ComponentType, type FunctionComponent } from 'preact'
import type { FeedbackStatus, ListQuery } from '@feedbaacks/sdk'
import type { Filters, StatusGroup } from './context'
import type { Translate } from './i18n'

/** Classes conditionnelles. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

/** Groupe de statut (filtres) → statuts envoyés à l'API. */
export function statusGroupToStatuses(group: StatusGroup): FeedbackStatus[] | undefined {
  switch (group) {
    case 'active':
      return ['open', 'under_review', 'planned', 'in_progress']
    case 'open':
      return ['open', 'under_review', 'planned']
    case 'in_progress':
      return ['in_progress']
    case 'on_hold':
      return ['on_hold']
    case 'shipped':
      return ['shipped']
    case 'declined':
      return ['declined', 'duplicate']
    default:
      return undefined
  }
}

/** Filtres de l'interface → requête de liste. */
export function filtersToQuery(f: Filters, offset: number, limit: number): ListQuery {
  return {
    type: f.type === 'all' ? undefined : [f.type],
    status: f.unread ? undefined : statusGroupToStatuses(f.statusGroup),
    q: f.search.trim().length >= 3 ? f.search.trim() : undefined,
    mine: f.mine || undefined,
    others: f.others || undefined,
    unread: f.unread || undefined,
    sort: f.sort,
    archived: f.archived === 'active' ? undefined : f.archived,
    include_deleted: f.trash || undefined,
    author_id: f.authorId ?? undefined,
    limit,
    offset: offset || undefined,
  }
}

const UNITS: Array<[number, Intl.RelativeTimeFormatUnit]> = [
  [60_000, 'second'],
  [3_600_000, 'minute'],
  [86_400_000, 'hour'],
  [7 * 86_400_000, 'day'],
  [30 * 86_400_000, 'week'],
  [365 * 86_400_000, 'month'],
]
const DIVISORS: Record<string, number> = { second: 1000, minute: 60_000, hour: 3_600_000, day: 86_400_000, week: 7 * 86_400_000, month: 30 * 86_400_000, year: 365 * 86_400_000 }

/** Date relative compacte (« 3 h », « il y a 2 j ») dans la langue choisie. */
export function formatRelative(iso: string, locale: string, t: Translate, now = Date.now()): string {
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return ''
  const diff = Math.max(0, now - ts)
  if (diff < 60_000) return t('relative_now')
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'narrow' })
  let unit: Intl.RelativeTimeFormatUnit = 'year'
  for (const [limit, u] of UNITS) {
    if (diff < limit) {
      unit = u
      break
    }
  }
  return rtf.format(-Math.floor(diff / DIVISORS[unit]), unit)
}

/** Date absolue lisible (« 30 juin 2026 à 14:33 »). */
export function absoluteDate(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Nomme la page d'où vient un retour : le chemin, coupé par la droite. */
export function describePageUrl(url: string | undefined): string | null {
  if (!url) return null
  try {
    const p = new URL(url).pathname
    return p.length > 30 ? `${p.slice(0, 29)}…` : p
  } catch {
    return null
  }
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

/** memo sans preact/compat : la ligne ne se re-rend que si ses props
 *  changent (la liste en compte des centaines, cf. Faast 566830b1). */
export function memo<P extends object>(Comp: FunctionComponent<P>): ComponentType<P> {
  class Memo extends Component<P> {
    shouldComponentUpdate(next: P): boolean {
      const prev = this.props as Record<string, unknown>
      const n = next as Record<string, unknown>
      for (const k in n) if (prev[k] !== n[k]) return true
      for (const k in prev) if (!(k in n)) return true
      return false
    }
    render(props: P) {
      return h(Comp, props)
    }
  }
  return Memo
}
